import type { ReactNode } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { AuthProvider } from "@/auth"
import { MainLayout } from "@/components/layout/MainLayout"
import { DashboardPage } from "@/pages/DashboardPage"
import { EnergieartenPage } from "@/pages/EnergieartenPage"
import { ProduktionsdatenPage } from "@/pages/ProduktionsdatenPage"
import { SeuPage } from "@/pages/SeuPage"
import { ZaehlerDetailPage } from "@/pages/ZaehlerDetailPage"
import { ZaehlerListPage } from "@/pages/ZaehlerListPage"
import { useAuthStore } from "@/store/auth"
import { ToastProvider } from "@/lib/toast"

const queryClient = new QueryClient()

// Die Module des Energie-Programmteils (ARCHITEKTUR.md §1, Feature-Schnitt 04)
export const MODULE = [
  { pfad: "zaehler", titel: "Zähler", untertitel: "Zählerstamm, Hierarchie, manuelle Erfassung (F2)" },
  { pfad: "energiearten", titel: "Energiearten & Preise", untertitel: "Lückenlose Preishistorie (F3)" },
  { pfad: "seu", titel: "SEU / ISO 50001", untertitel: "Significant Energy Use Reporting (F4)" },
  { pfad: "produktionsdaten", titel: "Produktionsdaten", untertitel: "Tageskennzahlen (F5)" },
] as const

/** Route-Guard: sperrt operative Module fuer reine Geschaeftsfuehrungs-Leser. */
function SeuOnlyGuard({ children }: { children: ReactNode }) {
  const hasOnlySeuAccess = useAuthStore((s) => s.hasOnlySeuAccess())
  if (hasOnlySeuAccess) {
    return <Navigate to="/seu" replace />
  }
  return <>{children}</>
}

function StartRedirect() {
  const hasOnlySeuAccess = useAuthStore((s) => s.hasOnlySeuAccess())
  return <Navigate to={hasOnlySeuAccess ? "/seu" : "/dashboard"} replace />
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <AuthProvider>
            <Routes>
              <Route element={<MainLayout />}>
                <Route
                  path="/dashboard"
                  element={
                    <SeuOnlyGuard>
                      <DashboardPage />
                    </SeuOnlyGuard>
                  }
                />
                <Route
                  path="/zaehler"
                  element={
                    <SeuOnlyGuard>
                      <ZaehlerListPage />
                    </SeuOnlyGuard>
                  }
                />
                <Route
                  path="/zaehler/:id"
                  element={
                    <SeuOnlyGuard>
                      <ZaehlerDetailPage />
                    </SeuOnlyGuard>
                  }
                />
                <Route
                  path="/energiearten"
                  element={
                    <SeuOnlyGuard>
                      <EnergieartenPage />
                    </SeuOnlyGuard>
                  }
                />
                <Route path="/seu" element={<SeuPage />} />
                <Route
                  path="/produktionsdaten"
                  element={
                    <SeuOnlyGuard>
                      <ProduktionsdatenPage />
                    </SeuOnlyGuard>
                  }
                />
                <Route path="*" element={<StartRedirect />} />
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  )
}
